import { Content } from '@wajeez-web/core/api-base';
import {
  FieldValues,
  UseControllerProps,
  UseFormSetError,
} from 'react-hook-form';

import { FormField } from './Types';
import FormTextInput from './Inputs/FormTextInput';
import FormMappedFieldInput from './Inputs/FormMappedFieldInput';
import FormPredefinedComponentInput from './Inputs/FormPredefinedComponentInput';
import FormRequiredComponentInput from './Inputs/FormRequiredComponentInput';
import FormUploadInput from './Inputs/FormUploadInput';
import FormCallBackSelectInput from './Inputs/FormCallBackSelectInput';
import FormTinyMCEInput from './Inputs/FormTinyMCEInput';
import FormUploadImageInput from './Inputs/FormUploadImageInput';
import FormSwitchInput from './Inputs/FormSwitchInput';
import FormNumberInput from './Inputs/FormNumberInput';
import FormSelectInput from './Inputs/FormSelectInput';
import FormDateInput from './Inputs/FormDateInput';
import FormFileDragAndDropInput from './Inputs/FormFileDragAndDropInput';
import FormAreaTextInput from './Inputs/FormAreaTextInput';
import FormColorPickerInput from './Inputs/FormColorPickerInput';
import FormDynamicTextInput from './Inputs/FormDynamicTextInput';

export interface WajeezFormInputProps<TFormFields extends FieldValues>
  extends UseControllerProps<TFormFields> {
  type: FormField;
  isReadOnly?: boolean;
  setError?: UseFormSetError<TFormFields>;
  validationErrors?: Partial<{ required: string }>;
  id?: string;
  isRtl?: boolean;
}

const WajeezFormInput = <TFormFields extends FieldValues>({
  type,
  isReadOnly,
  isRtl = true,
  ...props
}: WajeezFormInputProps<TFormFields>) => {
  const { fieldType, fieldOptions } = type;
  const disabled = isReadOnly || type.disabled;

  switch (fieldType) {
    case Content.FieldType.MappedField:
      return (
        <FormMappedFieldInput disabled={disabled} fieldType={type} {...props} />
      );
    case Content.FieldType.PredefinedComponent:
      return (
        <FormPredefinedComponentInput
          disabled={disabled}
          fieldType={type}
          {...props}
        />
      );
    case Content.FieldType.RequiredComponent:
      return (
        <FormRequiredComponentInput
          disabled={disabled}
          fieldType={type}
          {...props}
        />
      );
    case Content.FieldType.UploadFile:
      return (
        <FormUploadInput disabled={disabled} fieldType={type} {...props} />
      );
    case Content.FieldType.CallbackSelect:
      return (
        <FormCallBackSelectInput
          disabled={disabled}
          fieldType={type}
          {...props}
        />
      );
    //   case Content.FieldType.CallbackSelect:
    //     return (
    //       <CallBackSelectorWithOptions
    //           disabled={
    //   permission && !permissions.includes(permission) ? true : false
    // }
    //         required={required}
    //         prefix={fieldOptions.prefix || ''}
    //         isMulti={fieldOptions.isMulti}
    //         // @ts-ignore
    //         getOptions={fieldOptions.optionsListEndPiont}
    //         value={values[name] || undefined}
    //         label={label}
    //         name={name || ''}
    //         placeholder={placeHolder}
    //         onChange={onChange}
    //       />
    //     );
    case Content.FieldType.TineMCE:
      return (
        <FormTinyMCEInput
          disabled={isReadOnly}
          fieldType={type}
          isRtl={isRtl}
          {...props}
        />
      );
    case Content.FieldType.UploadImage:
      return (
        <FormUploadImageInput disabled={disabled} fieldType={type} {...props} />
      );
    case Content.FieldType.Switch:
      return (
        <FormSwitchInput disabled={disabled} fieldType={type} {...props} />
      );
    case Content.FieldType.Number:
      return (
        <FormNumberInput disabled={disabled} fieldType={type} {...props} />
      );
    case Content.FieldType.Select:
      return (
        <FormSelectInput disabled={disabled} fieldType={type} {...props} />
      );
    case Content.FieldType.DragFile:
      return (
        <FormFileDragAndDropInput
          disabled={disabled}
          fieldType={type}
          {...props}
        />
      );
    case Content.FieldType.Date:
      return (
        <FormDateInput
          disabled={disabled}
          fieldType={type}
          showTime={false}
          {...props}
        />
      );
    case Content.FieldType.DateTime:
      return (
        <FormDateInput
          disabled={disabled}
          fieldType={type}
          showTime={true}
          {...props}
        />
      );
    case Content.FieldType.AreaText:
      return (
        <FormAreaTextInput disabled={disabled} fieldType={type} {...props} />
      );
    case Content.FieldType.Text:
      return !type.fieldOptions.dynamic ? (
        <FormTextInput
          disabled={disabled}
          fieldType={type}
          errorMessages={{ required: 'This field is required' }}
          isRtl={isRtl}
          {...props}
        />
      ) : (
        <FormDynamicTextInput
          disabled={disabled}
          fieldType={type}
          errorMessages={{ required: 'This field is required' }}
          isRtl={isRtl}
          {...props}
        />
      );
    case Content.FieldType.ColorPicker:
      return (
        <FormColorPickerInput
          disabled={disabled}
          fieldType={type}
          colors={fieldOptions.colors}
          pickerType={fieldOptions.pickerType}
          {...props}
        />
      );

    default:
      return null;
  }
};

export default WajeezFormInput;
